import { z } from 'zod';
import { flow, pipe } from 'fp-ts/function';
import { BookablePeriod, CancellationFeeExtent, CancellationPolicyApplicability } from '@mews-commander/data-access';
import { DecimalService, DurationService, Validation } from '@mews/framework';
import { MAX_SMALL_INPUT_TEXT_LENGTH } from '@mews-commander/constants';
import { CancellationPolicyFormField, CancellationPolicyOffsetType, CancellationFeeExtentFieldOption } from '../types';

const MAX_ABSOLUTE_FEE_AMOUNT = 450_000;
const MAX_FEE_TIME_UNITS = 999_999;
const MAX_APPLICABILITY_OFFSET = 1500;
const PERCENTAGE_PRECISION = 2;
const MAX_POLICY_NAME_INPUT_TEXT_LENGTH = 200;

const durationUnitSchema = Validation.Number.MaxNonNegativeInteger.max(MAX_APPLICABILITY_OFFSET).default(0);

const durationSchema = z
    .object({
        months: durationUnitSchema,
        days: durationUnitSchema,
        hours: durationUnitSchema,
        minutes: durationUnitSchema,
        seconds: durationUnitSchema,
    })
    .transform(flow(DurationService.fromObject, DurationService.toString));

const percentageToDecimalValue = (percentage: number, precision = PERCENTAGE_PRECISION) => {
    const rounded = DecimalService.round(DecimalService.parse(percentage), precision);
    const product = DecimalService.divide(rounded, DecimalService.parse(100));
    return DecimalService.toNumber(product);
};

const relativeFeeSchema = Validation.Number.Percentage.transform((value) => percentageToDecimalValue(value));

const nullableStringSchema = z
    .string()
    .max(MAX_SMALL_INPUT_TEXT_LENGTH)
    .nullish()
    .transform((value) => (value === '' ? null : value));

const applicabilitySchema = z.object({
    [CancellationPolicyFormField.ApplicabilityOffset]: durationSchema,
    [CancellationPolicyFormField.OffsetType]: z.nativeEnum(CancellationPolicyOffsetType),
});

export const transformApplicabilityOffset = <
    T extends { applicabilityOffset: string; offsetType: CancellationPolicyOffsetType },
>({
    applicabilityOffset,
    offsetType,
    ...other
}: T) => {
    const applicabilityOffsetDuration = DurationService.parse(applicabilityOffset);
    return {
        ...other,
        applicabilityOffset:
            offsetType === CancellationPolicyOffsetType.Before
                ? pipe(applicabilityOffsetDuration, DurationService.negate, DurationService.toString)
                : pipe(applicabilityOffsetDuration, DurationService.toString),
    };
};

export const baseValidationSchema = z
    .object({
        [CancellationPolicyFormField.AbsoluteFee]: Validation.Number.MaxNonNegativeInteger.max(MAX_ABSOLUTE_FEE_AMOUNT),
        [CancellationPolicyFormField.Name]: z
            .string()
            .max(MAX_POLICY_NAME_INPUT_TEXT_LENGTH)
            .nullish()
            .transform((value) => (value === '' ? null : value)),
        [CancellationPolicyFormField.Key]: nullableStringSchema,
        [CancellationPolicyFormField.RelativeFee]: relativeFeeSchema,
        [CancellationPolicyFormField.FeeMaximumTimeUnits]: z
            .union([z.number().int().min(1).max(MAX_FEE_TIME_UNITS), z.literal('')])
            .nullish()
            .transform((value) => (value === '' ? null : value)),
        [CancellationPolicyFormField.ApplicabilityOffset]: durationSchema,
        [CancellationPolicyFormField.OffsetType]: z.nativeEnum(CancellationPolicyOffsetType),
    })
    .merge(applicabilitySchema);

export const validationSchema = baseValidationSchema
    .extend({
        [CancellationPolicyFormField.AbsoluteFeeCurrencyCode]: z.string(),
        [CancellationPolicyFormField.Applicability]: z.nativeEnum(CancellationPolicyApplicability),
        [CancellationPolicyFormField.BookablePeriod]: z.nativeEnum(BookablePeriod),
        [CancellationPolicyFormField.FeeExtent]: z.nativeEnum(CancellationFeeExtentFieldOption).transform((value) => {
            switch (value) {
                case CancellationFeeExtentFieldOption.TimeUnits:
                    return [CancellationFeeExtent.TimeUnits];
                case CancellationFeeExtentFieldOption.Products:
                    return [CancellationFeeExtent.Products];
                case CancellationFeeExtentFieldOption.TimeUnitsAndProducts:
                    return [CancellationFeeExtent.TimeUnits, CancellationFeeExtent.Products];
            }
        }),
    })
    .transform(transformApplicabilityOffset);

export type BaseFormValues = z.input<typeof baseValidationSchema>;
export type FormValues = z.input<typeof validationSchema>;
